




import modalCodes from '@store/modules/ui/shared/modal/modalCodes';
import { useSurveyRenderManager } from './SurveyRenderManager';
import { FormConfiguration } from './FormConfiguration';
import { SpecificSurveyPageSetup } from '@src/config/shell/remoteConfigTypes';
import { useNotificationsStore } from '@modules/notifications/store';
import { capitalize } from '@superbet-group/web.lib.utilities';
import {
    computed,
    defineComponent,
    PropType,
    ref,
} from '@vue/composition-api';
import {
    useI18n,
    useVuexStore,
} from '@superbet-group/web.lib.vue-utilities';
import { ModalStore } from '@store/modules/ui/shared/modal/modalIndex';
import { AuthenticationStore } from '@store/modules/data/authentication/authenticationIndex';
import { useGetFeatureFlag } from '@src/app/shared/featureFlags/useGetFeatureFlag';
import { ExperimentSurveyFlag } from '@core/models/user/flags';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { useSurveyService } from './SurveyService';

function checkSurveyActive(formConfiguration: FormConfiguration): boolean {
    if (!formConfiguration.active) {
        return false;
    }

    const now = new Date();
    if (formConfiguration.activeFrom) {
        const activeFrom = new Date(formConfiguration.activeFrom);
        if (now < activeFrom) {
            return false;
        }
    }
    if (formConfiguration.activeTo) {
        const activeTo = new Date(formConfiguration.activeTo);
        if (now > activeTo) {
            return false;
        }
    }
    return true;
}

function getFormattedUserFirstName(userFirstName: string): string {
    return userFirstName ? capitalize(userFirstName.toLocaleLowerCase()) : '';
}

export default defineComponent({
    name: 'Survey',
    props: {
        surveyPageSetup: {
            type: Object as PropType<SpecificSurveyPageSetup | undefined>,
            required: false,
            default: null,
        },
        checkSurveyDisplayCondition: {
            type: Function as PropType<() => boolean>,
            required: false,
            default: null,
        },
    },
    setup(props) {
        const { translate } = useI18n();

        const authModule = useVuexStore<AuthenticationStore>('data/authentication').unpack();
        const modalModule = useVuexStore<ModalStore>('ui/modal');

        const addModalToEnd = modalModule.action('addModalToEnd');
        const user = authModule.user;
        const formattedUserFirstName = computed(() => getFormattedUserFirstName(user.value?.firstName ?? ''));

        const formConfiguration = ref<FormConfiguration | null>(null);

        const [
            surveyVariant,
            onSurveyVariantReady,
        ] = useGetFeatureFlag<ExperimentSurveyFlag>(
            props.surveyPageSetup?.flagName ?? '',
            'None',
        );

        onSurveyVariantReady(async (variant) => {
            if (variant === 'None') {
                return;
            }
            if (!user.value
                || !props.surveyPageSetup
                || (props.checkSurveyDisplayCondition && !props.checkSurveyDisplayCondition())) {
                return;
            }
            const surveyService = useSurveyService(
                props.surveyPageSetup,
                props.surveyPageSetup.surveyId ?? '',
                user.value?.userId.toString() ?? '',
            );

            const surveyLocalStorageManager = useSurveyRenderManager(
                props.surveyPageSetup.surveyId ?? '',
                user.value?.userId.toString() ?? '',
                props.surveyPageSetup,
            );

            if (!surveyLocalStorageManager.enableSurveyDisplay()) {
                return;
            }

            try {
                formConfiguration.value = await surveyService.fetchSurveyData();
                if (!formConfiguration.value || !checkSurveyActive(formConfiguration.value)) {
                    return;
                }
            } catch (error) {
                console.error('Error fetching survey data:', error);
                return;
            }

            AnalyticsSDK.getInstance().log(
                'Survey_Seen', {
                    surveyId: props.surveyPageSetup.surveyId,
                    variant: variant,
                });

            if (variant === 'A') {
                return;
            }
            surveyLocalStorageManager.setNextDisplayTime();
            renderSurvey();
        });

        function showSurveyModal(
        ) {
            addModalToEnd({
                code: modalCodes.surveyModalCode,
                data: {
                    surveyTitleLokKey: props.surveyPageSetup?.titleTranslationKey ?? '',
                    userData: user.value,
                    formattedUserFirstName: formattedUserFirstName.value,
                    formConfiguration: formConfiguration.value,
                    surveyId: props.surveyPageSetup?.surveyId ?? '',
                    surveyPageSetup: props.surveyPageSetup,
                    variant: surveyVariant.value,
                },
            });
        }

        function renderSurvey(): void {
            if (!props.surveyPageSetup) {
                return;
            }
            switch (surveyVariant.value) {
                case 'A':
                    break;
                case 'C':
                    setTimeout(() => {
                        showSurveyModal();
                    }, props.surveyPageSetup.delayBeforeSurveyPopupMS || 50000);
                    break;
                case 'D':
                    useNotificationsStore().showNotification({
                        title: translate('survey-answerrequest-snackbar-title', {
                            userFirstName: formattedUserFirstName.value,
                        }),
                        message: translate('survey-answerrequest-snackbar-message'),
                        timeoutMs: props.surveyPageSetup.snackbaranswerRequestTimeoutMS || 5000,
                        actionButton: {
                            buttonText: translate('survey-answerrequest-snackbar-action'),
                            onClick: () => {
                                showSurveyModal();
                            },
                        },
                    });
                    break;
                default:
                    showSurveyModal();
                    break;
            }
        }
    },
    components: { },
});

